import React, { Suspense, lazy } from "react";
import { Provider, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import { css } from "@emotion/core";
import { Spin, Icon } from "antd";
import { store, persistor } from "./reducer";
import { PersistGate } from "redux-persist/integration/react";
import AppLayoutContainer from "./container/AppLayoutContainer";
import ScrollToTop from "./component/ScrollToTop/ScrollToTop";
import SystemState from "./type";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const AsyncAccountPage = lazy(() => import("./page/AccountPage"));
const AsyncUsersPage = lazy(() => import("./page/UsersPage"));
const AsyncPrivacyPolicyPage = lazy(() => import("./page/PrivacyPrivacyPage"));
const AsyncLeadPage = lazy(() => import("./page/LeadPage"));
const AsyncHelpPage = lazy(() => import("./page/HelpPage"));
const AsyncInventoryPage = lazy(() => import("./page/InventoryPage"));
const AsyncHomePage = lazy(() => import("./container/HomePageContainer"));
const AsyncLoginPage = lazy(() => import("./page/LoginPage"));
const AsyncSignupPage = lazy(() => import("./page/SignUpPage"));
const AsyncForgetPasswordPage = lazy(() => import("./page/ForgetPasswordPage"));
const AsyncResetPasswordPage = lazy(() => import("./page/ResetPasswordPage"));
const AsyncSellPage = lazy(() => import("./page/SellPage"));
const AsyncCarOfferPage = lazy(() => import("./page/CreateCarCashOfferPage"));
const AsyncBoatOfferPage = lazy(() => import("./page/CreateBoatCashOfferPage"));
const AsyncCycleProSearchPage = lazy(() => import("./page/CycleProSearchPage"));
const AsyncMotorcycleOfferPage = lazy(() =>
  import("./page/CreateMotorcycleOfferPage")
);
const AsyncAboutUsPage = lazy(() => import("./page/AboutUsPage"));
const AsyncTermsPage = lazy(() => import("./page/TermsPage"));
const AsyncPortalPage = lazy(() => import("./page/PortalPage"));
const AsyncAnalyticsPage = lazy(() => import("./page/AnalyticsPage"));
const AsyncAuthVerify = lazy(() => import("./component/Home/AuthVerify"));
const AsyncNadaDecoder = lazy(() => import("./page/NadaDecoder"));
const AsyncYearAndModel = lazy(() => import("../src/component/NadaDecoder/YearModel/YearandModel"));
const AsyncSelectOptions = lazy(() => import("../src/component/NadaDecoder/SelectOptions/SelectOptions"));


const loadGTM = (gtmId:any) => {
  // Add the <script> tag for GTM
  // const script = document.createElement('script');
  // script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  // script.async = true;
  // document.body.appendChild(script);

  const script1 = document.createElement('script');
  script1.innerHTML = `
  (function(w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l !== 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', '${gtmId}');
  `;
  document.head.appendChild(script1);

  // Optionally add the <noscript> iframe fallback for non-JS users
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `
    <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>
  `;
  document.body.appendChild(noscript);
};


const getPortalIdFromUrl = (): string | null => {
  const match = window.location.pathname.match(/\/portal\/(\d+)/);
  return match ? match[1] : null;
};

const portalGTMMap: Record<string, string> = {
  '1654': 'GTM-KSHXT7',
  '656': 'GTM-WRS5VGS',
  '2682': 'GTM-WV634KTP',
  '1787': 'GTM-KVJ3ZK',
  '261': 'GTM-NLRDC3K',
  '1745': 'GTM-NNWM3XT',
  '1758': 'GTM-P9KC5Q',
  '281': 'GTM-T7MLWT7',
  '1765': 'GTM-PDQP8S8',
  '1768': 'GTM-5QCD6N',
  '195': 'GTM-WQQ9TM',
  '1736': 'GTM-W99V3KM',
  '1741': 'GTM-KB4L5S',
  '1784': 'GTM-MSNNWV',
  '1740': 'GTM-MLCLWF6',
  '1788': 'GTM-MQV7S5R',
  '2679': 'GTM-N6GBC53F',
  '1780': 'GTM-N9L3L8',
  '1778': 'GTM-MQJZ63C',
  '1739': 'GTM-MP2D5DP',
  '1777': 'GTM-M5VCV6',
  '2681': 'GTM-WNZG74ZM',
  '1763': 'GTM-WCH7DMC',
  '2388': 'GTM-MZXSK4Q',
  '2191': 'GTM-K3FRPZ3',
  '664': 'GTM-WLH2F65',
  '1843': 'GTM-KSHXT7',
  '1762': 'GTM-P5DW28',
  '1781': 'GTM-K6S3NWB',
  '658': 'GTM-N6JNHJQ',
  '657': 'GTM-WVSBCVC',
  '1221': 'GTM-P9RDB7D',
  '280': 'GTM-KXKPLTV',
  '1771': 'GTM-MFJW4K',
  '1773': 'GTM-5VR5XKT',
  '2680': 'GTM-WBW88MW6',
  '2192': 'GTM-NJQR4D7',
  '1790': 'GTM-NVTG8HH',
  '1779': 'GTM-5TWQSZN',
  '661': 'GTM-K29PFX4',
  '1669': 'GTM-K3CKFNF',
  '662': 'GTM-PQT44N7',
  '1775': 'GTM-TZJ84S9',
  '1786': 'GTM-MHRC8SP',
  '1754': 'GTM-5BTTB3',
  '1750': 'GTM-MXWG9P',
  '1746': 'GTM-PL8LJTK',
  '1766': 'GTM-5QCG352',
  '1785': 'GTM-5NK5CDB',
  '1761': 'GTM-58RQZNT',
  '2676': 'GTM-NJLBHPN',
  '1735': 'GTM-NDSD3P',
  '1749': 'GTM-5PKZB2J',
  '1782': 'GTM-KJW5XH7',
};

const portalId = getPortalIdFromUrl();

let gtmId: string | undefined;

if (portalId) {
  gtmId = portalGTMMap[portalId];
}

if (gtmId) {
  // Load GTM
  loadGTM(gtmId);
} else {
  console.warn('No GTM container mapped for this portal ID.');
}

const root: HTMLElement | null = document.getElementById("root");
if (root) {
  root.style.height = "100%";
}

const PrivateRoute: React.FC<{
  component: React.FC;
  path: string;
  exact?: boolean;
}> = props => {
  const isLoggedIn = useSelector((state: SystemState) => state.auth.isLoggedIn);
  return isLoggedIn ? (
    <Route path={props.path} exact={props.exact} component={props.component} />
  ) : (
    <Redirect to="/" />
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <ScrollToTop>
            <AppLayoutContainer>
              <Suspense
                fallback={
                  <div
                    css={css`
                      text-align: center;
                      padding-top: 35vh;
                      min-height: 75vh;
                      min-width: 80vw;
                    `}
                  >
                    <Spin
                      spinning
                      size="large"
                      indicator={<Icon type="loading-3-quarters" spin />}
                    />
                  </div>
                }
              >
                <Switch>
                  <Route path="/" exact component={AsyncHomePage} />
                  {/* below link is to handle IAS login from old v2 */}
                  <Route path="/auth/sign-in" component={AsyncLoginPage} />
                  <Route
                    path="/auth/reset-password"
                    component={AsyncResetPasswordPage}
                  />

                  <Route path="/login" component={AsyncLoginPage} />
                  <Route path="/signup" component={AsyncSignupPage} />
                  <Route
                    path="/forgetpassword"
                    component={AsyncForgetPasswordPage}
                  />
                  <Route path="/sell" component={AsyncSellPage} />
                  <Route
                    path="/cycleprosearch"
                    component={AsyncCycleProSearchPage}
                  />
                  <Route
                    path="/create-car-offer"
                    component={AsyncCarOfferPage}
                  />
                  <Route
                    path="/create-boat-offer"
                    component={AsyncBoatOfferPage}
                  />
                  <Route
                    path="/create-motorcycle-offer"
                    component={AsyncMotorcycleOfferPage}
                  />
                  <Route path="/about" component={AsyncAboutUsPage} />
                  <Route path="/terms" component={AsyncTermsPage} />
                  <Route
                    path="/others/privacy-policy"
                    component={AsyncPrivacyPolicyPage}
                  />
                  {/* <PrivateRoute
                    path="/analytics"
                    component={AsyncAnalyticsPage}
                  /> */}
                  <PrivateRoute
                    path="/leads/:leadId"
                    component={AsyncLeadPage}
                  />

                  <PrivateRoute path="/users" component={AsyncUsersPage} />

                  <PrivateRoute path="/leads" component={AsyncLeadPage} />

                  <PrivateRoute path="/help" component={AsyncHelpPage} />
                  <PrivateRoute
                    path="/inventory"
                    component={AsyncInventoryPage}
                  />
                  <PrivateRoute path="/account" component={AsyncAccountPage} />
                  {/* <PrivateRoute
                    path="/analytics/dealership"
                    component={AsyncAnalyticsPage}
                  />
                  <PrivateRoute
                    path="/analytics/click-through-rate"
                    component={AsyncAnalyticsPage}
                  />
                  <PrivateRoute
                    path="/analytics/inventory-metric"
                    component={AsyncAnalyticsPage}
                  />
                  <PrivateRoute
                    path="/analytics/advanced-inventory-metric"
                    component={AsyncAnalyticsPage}
                  /> */}
                  <Route
                    path="/portal/boat/:accountId/:redirect?"
                    component={AsyncPortalPage}
                  />
                  <Route
                    path="/portal/:accountId/:redirect?"
                    component={AsyncPortalPage}
                  />
                  <Route
                    path="/nadadecoder"
                    component={AsyncNadaDecoder}
                  />
                  <Route 
                    path="/Select-Year-Model/:makeid/:vehiclecategory/:vehicletype"
                    component={AsyncYearAndModel}
                  />
                  <Route 
                    path="/Select-Options/:categoryid/:makeid/:modelid/:vehicletype"
                    component={AsyncSelectOptions}
                  />
                </Switch>
                <AsyncAuthVerify />
              </Suspense>
            </AppLayoutContainer>
          </ScrollToTop>
        </PersistGate>
      </Provider>
    </Router>
  );
};

export default App;
